import { useEffect, useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Topbar } from './Topbar';
import { Sidebar } from './Sidebar';
import { UndoBar } from './UndoBar';
import { useAuthStore, useEmployeeOnly } from '@/store/auth';
import { useEntityStore } from '@/store/entity';
import { useNavMenu } from '@/api/nav-menu';
import { PageNoAccess } from './PageNoAccess';
import { useMyAccess } from '@/api/authz';
import { APP_BASE } from '@/lib/api-base';
import { endSession, expireIfStale } from '@/lib/session';

const SIDEBAR_WIDTH = 250;   // wireframe --sidebar-width
const TOPBAR_HEIGHT = 60;    // wireframe --header-height

/**
 * App shell — flex layout matching the legacy `.shell + .sb + .main`
 * structure. Three rules to remember:
 *
 *  1. Outer is `height: 100vh` + `overflow: hidden` so the page never
 *     scrolls at the window level. Only the main content area scrolls.
 *  2. Sidebar is a flex item with fixed width + `flex-shrink: 0`. Not
 *     position:fixed.
 *  3. Main column is `flex: 1` + `min-width: 0` (the latter is essential
 *     so wide tables don't push the column wider than its parent).
 *
 * On mobile (< md) the fixed sidebar would swallow most of the screen, so it
 * becomes an off-canvas Drawer opened from a hamburger in the topbar and
 * dismissed on navigation / backdrop tap.
 */
export function AppShell() {
  const isAuthed = useAuthStore((s) => s.isAuthenticated);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const employeeOnly = useEmployeeOnly();
  const access = useMyAccess(userId, isAuthed && employeeOnly);
  // The query the sidebar reads too, so this costs no extra request.
  const menu = useNavMenu(useEntityStore((s) => s.entityId), isAuthed && !employeeOnly);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // A staff page none of the user's roles opens, or a page below it: say so instead of rendering a page whose
  // every request is refused. Held until the menu answers, so it never flashes; a failed menu leaves the pages to
  // the server, which refuses the same requests.
  const path = location.pathname;
  const sub = path.slice(APP_BASE.length) || '/';
  const denied = menu.data?.denied ?? [];
  const blocked = denied.some((r) => (r ? sub === r || sub.startsWith(`${r}/`) : sub === '/'));
  // The menu is kept for minutes, so a page it refuses is checked against a fresh one: access just given shows at
  // once instead of when the menu goes stale.
  const { refetch } = menu;
  useEffect(() => { if (blocked) void refetch(); }, [blocked, path, refetch]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // A click into another page is the moment a stale session would otherwise
  // surface as a failed request — check the token first and go to sign-in.
  useEffect(() => { expireIfStale(); }, [location.pathname]);

  if (!isAuthed) {
    return <Navigate to={`${APP_BASE}/login`} replace state={{ from: location }} />;
  }

  // Neither staff access nor any self-service right (a leaver past the grace
  // window, say): every self-service page would only be refused, so say so once.
  if (employeeOnly && access.data && !access.data.permissions.some((k) => k.startsWith('ess.'))) {
    return <NoAccess />;
  }

  // Employees land on /me. Blocking other admin paths is handled by
  // individual pages and the role-aware sidebar that hides them.
  const isEss = path.startsWith(`${APP_BASE}/me`);
  if (employeeOnly && !isEss) {
    return <Navigate to={`${APP_BASE}/me`} replace />;
  }
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',          // never grow — single scroll surface
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      {isMobile ? (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { border: 0, width: SIDEBAR_WIDTH } }}
        >
          <Sidebar width={SIDEBAR_WIDTH} inDrawer onNavigate={() => setMobileOpen(false)} />
        </Drawer>
      ) : (
        <Sidebar width={SIDEBAR_WIDTH} />
      )}

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,             // CRITICAL — lets DataGrid etc. constrain to width
          minHeight: 0,
        }}
      >
        <Topbar
          height={TOPBAR_HEIGHT}
          showMenuButton={isMobile}
          onMenuClick={() => setMobileOpen(true)}
        />
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            overflowX: 'auto',     // local horizontal scroll only when content actually exceeds
            // Always reserve the scrollbar's width, so a tall page (or tab)
            // and a short one lay out at the same width — otherwise the
            // header and cards jump sideways when switching between them.
            scrollbarGutter: 'stable',
            p: { xs: 1.5, sm: 2, md: 2.5 },
          }}
        >
          {/* Wireframe `.page-body` — page content sits on a white rounded
              card inset from the grey page background. Content fades in on
              each route change. */}
          <Box
            key={location.pathname}
            sx={{
              bgcolor: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e1e5e8',
              minHeight: '100%',
              p: { xs: 1.75, sm: 2.5, md: 3 },
              minWidth: 0,
              animation: 'uknPageIn 260ms ease',
              '@keyframes uknPageIn': {
                from: { opacity: 0, transform: 'translateY(6px)' },
                to: { opacity: 1, transform: 'none' },
              },
            }}
          >
            {menu.isLoading ? null : blocked ? <PageNoAccess home={!denied.includes('')} /> : <Outlet />}
          </Box>
        </Box>
      </Box>
      <UndoBar />
    </Box>
  );
}

function NoAccess() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
               justifyContent: 'center', bgcolor: 'grey.100', p: 2 }}>
      <Box sx={{ maxWidth: 420, textAlign: 'center' }}>
        <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
          You don&apos;t have access to Payroll. Contact your Payroll administrator.
        </Alert>
        <Button variant="outlined" onClick={() => endSession('signed-out')}>Sign out</Button>
      </Box>
    </Box>
  );
}
